import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { CheckCircle, ExternalLink, AlertTriangle, Loader2, RefreshCw, Info } from 'lucide-react';
import { useAuth } from '../../App';
import { toast } from 'sonner';
import { translateStripeRequirements } from '../../lib/stripeRequirements';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STRIPE_RETURN_PATH = '/settings/stripe/return';
const STRIPE_REFRESH_PATH = '/settings/stripe/refresh';

const StripeConnectSettings = () => {
  const { business } = useAuth();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [providers, setProviders] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const country = business?.country;
  const stripeSupported = !!providers?.stripe_supported;

  // ----- Initial load -----
  useEffect(() => {
    if (!business) return;
    const load = async () => {
      try {
        const [provRes, statusRes] = await Promise.all([
          axios.get(`${API}/payments/providers/${country}`),
          axios.get(`${API}/stripe-connect/status`).catch(() => ({ data: { connected: false } })),
        ]);
        setProviders(provRes.data);
        setStatus(statusRes.data);
      } catch (e) {
        console.error('Stripe Connect: failed to load status:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [business, country]);

  // Iter 939 - OAuth ("Sign in with Stripe") is now the PRIMARY connect path
  // (Stripe approved AkountFly's Connect Client ID Feb 2026). Existing-Stripe
  // merchants click "Sign in with Stripe", authorize on Stripe's side with their
  // existing credentials, and land back here connected - no re-KYC.
  //
  // Account Links + Hosted Onboarding (Standard) is kept as a fallback for
  // merchants who don't yet have a Stripe account.
  const handleConnect = async () => {
    setActionLoading(true);
    try {
      const origin = window.location.origin;
      const returnUrl = `${origin}${STRIPE_RETURN_PATH}`;
      // PRIMARY: OAuth "Sign in with Stripe"
      try {
        const res = await axios.post(`${API}/stripe-connect/oauth/url`, {
          return_url: returnUrl,
        });
        if (res.data?.authorization_url) {
          window.location.href = res.data.authorization_url;
          return;
        }
      } catch (oauthErr) {
        // If OAuth is not configured (503) or country-gated (400), fall through
        // to Account Links. Country-gate errors are surfaced as-is to the user.
        const detail = oauthErr?.response?.data?.detail || '';
        const status = oauthErr?.response?.status;
        if (status === 400 && /not available in/i.test(detail)) {
          throw oauthErr;
        }
        // 503 (no client_id) or anything else → quietly try Account Links
        if (status !== 503) {
          console.warn('Stripe OAuth failed, falling back to Account Links:', detail);
        }
      }
      // FALLBACK 1: Account Links + Hosted Onboarding (Standard)
      const refreshUrl = `${origin}${STRIPE_REFRESH_PATH}`;
      try {
        const res = await axios.post(`${API}/stripe-connect/standard/onboard`, {
          return_url: returnUrl,
          refresh_url: refreshUrl,
        });
        if (res.data?.onboarding_url) {
          window.location.href = res.data.onboarding_url;
          return;
        }
      } catch (stdErr) {
        const detail = stdErr?.response?.data?.detail || '';
        if (stdErr?.response?.status === 400 && /not available in/i.test(detail)) {
          throw stdErr;
        }
      }
      // FALLBACK 2: legacy Express (creates a managed sub-account)
      const res = await axios.post(`${API}/stripe-connect/create-account`, {
        return_url: returnUrl,
        refresh_url: refreshUrl,
      });
      if (res.data?.onboarding_url) {
        window.location.href = res.data.onboarding_url;
      } else if (res.data?.message) {
        toast.success(res.data.message);
        const statusRes = await axios.get(`${API}/stripe-connect/status`);
        setStatus(statusRes.data);
      }
    } catch (e) {
      // iter928 - translate the Stripe "Connect not enabled" error into a
      // friendly message so users don't see raw Stripe wording.
      const rawDetail = e?.response?.data?.detail || '';
      if (/signed up for Connect/i.test(rawDetail) || /Connect.*dashboard/i.test(rawDetail)) {
        toast.error(
          'Stripe Connect is being activated for akountFly. Please check back in a few minutes: or use Bank Transfer in the meantime.',
          { duration: 7000 }
        );
      } else {
        toast.error(rawDetail || 'Failed to start Stripe onboarding.');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleDashboard = async () => {
    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/stripe-connect/create-login-link`);
      if (res.data?.url) {
        window.open(res.data.url, '_blank', 'noopener,noreferrer');
      } else {
        toast.error('Could not generate Stripe dashboard link.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not open Stripe dashboard.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRefreshStatus = async () => {
    setActionLoading(true);
    try {
      const res = await axios.get(`${API}/stripe-connect/status`);
      setStatus(res.data);
      toast.success('Status refreshed');
    } catch {
      toast.error('Could not refresh status');
    } finally {
      setActionLoading(false);
    }
  };

  // ----- Render -----
  if (loading) {
    return (
      <Card className="overflow-hidden">
        <CardContent className="p-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading Stripe status…
        </CardContent>
      </Card>
    );
  }

  // Country not supported by Stripe Connect
  if (!stripeSupported) {
    return (
      <Card className="overflow-hidden opacity-95" data-testid="stripe-connect-unsupported">
        <CardHeader className="bg-gradient-to-r from-[#635BFF]/15 to-[#635BFF]/5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#635BFF]/10 rounded-lg">
                <StripeLogo />
              </div>
              <div>
                <CardTitle className="text-[#635BFF] text-lg">Stripe Connect</CardTitle>
                <CardDescription>Global card payments via Stripe</CardDescription>
              </div>
            </div>
            <Badge variant="outline" className="border-slate-300 text-slate-600 bg-slate-50">
              Not available in {country || 'your country'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>Stripe Connect Express isn&apos;t natively available in {country}</AlertTitle>
            <AlertDescription className="mt-2 text-sm">
              For now, your invoices will use <strong>manual bank transfer</strong> with reference matching. We&apos;ll add a local provider for {country} soon.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const connected = !!status?.connected;
  const fullySetUp = connected && status?.charges_enabled && status?.payouts_enabled && status?.details_submitted;
  // iter1120 - distinguish "Stripe is verifying your details" (nothing user
  // can do) from "You still owe Stripe documents" (needs Resume Onboarding).
  const onboardingState = status?.onboarding_state; // fully_setup | under_review | action_needed | restricted | not_started | not_connected
  const underReview = connected && onboardingState === 'under_review';
  const restricted = connected && onboardingState === 'restricted';

  // iter1206 - Translate raw Stripe requirement codes into human labels so
  // users see EXACTLY what Stripe is waiting on.
  const rawOutstanding = [
    ...(status?.requirements?.past_due || []),
    ...(status?.requirements?.currently_due || []),
  ];
  const seenReqCodes = new Set();
  const dedupedReqCodes = rawOutstanding.filter((c) => {
    if (seenReqCodes.has(c)) return false;
    seenReqCodes.add(c);
    return true;
  });
  const outstandingItems = translateStripeRequirements(dedupedReqCodes);
  const isStandardConnect = status?.connect_mode === 'oauth_standard';

  // iter1206 - Resume Onboarding: for OAuth Standard accounts that have
  // submitted details but still have past_due requirements, route the
  // user to their Stripe Dashboard via the backend `/resume` endpoint
  // (which returns a login-link URL). Falls back to full handleConnect
  // for any other case.
  const handleResume = async () => {
    setActionLoading(true);
    try {
      const origin = window.location.origin;
      const returnUrl = `${origin}${STRIPE_RETURN_PATH}`;
      const refreshUrl = `${origin}${STRIPE_REFRESH_PATH}`;
      const res = await axios.post(`${API}/stripe-connect/resume`, {
        return_url: returnUrl,
        refresh_url: refreshUrl,
      });
      const url = res.data?.url;
      if (url) {
        if (res.data?.mode === 'dashboard_login') {
          window.open(url, '_blank', 'noopener,noreferrer');
        } else {
          window.location.href = url;
        }
        return;
      }
      await handleConnect();
    } catch {
      await handleConnect();
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Card className="overflow-hidden" data-testid="stripe-connect-card">
      <CardHeader className="bg-gradient-to-r from-[#635BFF]/20 to-[#635BFF]/5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#635BFF]/10 rounded-lg">
              <StripeLogo />
            </div>
            <div>
              <CardTitle className="text-[#635BFF] text-lg">Stripe Connect</CardTitle>
              <CardDescription>Global card payments, Apple Pay, Google Pay & local methods</CardDescription>
            </div>
          </div>
          {fullySetUp ? (
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 gap-1" data-testid="stripe-status-badge-connected">
              <CheckCircle className="w-3 h-3" /> Connected
            </Badge>
          ) : underReview ? (
            <Badge className="bg-blue-100 text-blue-800 border-blue-300 gap-1" data-testid="stripe-status-badge-under-review">
              <Loader2 className="w-3 h-3 animate-spin" /> Under review by Stripe
            </Badge>
          ) : restricted ? (
            <Badge className="bg-red-100 text-red-800 border-red-300 gap-1" data-testid="stripe-status-badge-restricted">
              <AlertTriangle className="w-3 h-3" /> Restricted
            </Badge>
          ) : connected ? (
            <Badge variant="outline" className="border-amber-400 text-amber-700 bg-amber-50 gap-1" data-testid="stripe-status-badge-incomplete">
              <AlertTriangle className="w-3 h-3" /> Onboarding incomplete
            </Badge>
          ) : (
            <Badge variant="outline" className="border-slate-300 text-slate-600 bg-slate-50" data-testid="stripe-status-badge-not-connected">
              Not connected
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="p-6 space-y-4">
        {/* Not connected - show pitch + connect button */}
        {!connected && (
          <>
            <p className="text-sm text-muted-foreground">
              Accept card payments globally with Stripe. Click <em>Sign in with Stripe</em> to securely
              link your existing Stripe account in one click (or create a new one), then come right back
              to AkountFly when you&apos;re done.
            </p>
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription className="text-xs">
                <strong>How it works:</strong> AkountFly uses Stripe Connect OAuth. On the next screen
                you sign in with your existing Stripe credentials and authorize AkountFly: your Stripe
                dashboard, payout schedule, and existing data stay exactly as they are. Funds collected
                via AkountFly invoices land directly in your Stripe account. AkountFly charges a 2%
                platform fee per successful invoice payment.
              </AlertDescription>
            </Alert>
            <Button
              onClick={handleConnect}
              disabled={actionLoading}
              className="bg-[#635BFF] hover:bg-[#5249e0] text-white"
              data-testid="stripe-connect-btn"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}
              Sign in with Stripe
            </Button>
          </>
        )}

        {/* iter1120 - Connected AND Stripe is verifying (nothing user can do) */}
        {connected && underReview && (
          <>
            <Alert className="border-blue-300 bg-blue-50" data-testid="stripe-under-review-alert">
              <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />
              <AlertTitle className="text-blue-900">Stripe is reviewing your details</AlertTitle>
              <AlertDescription className="text-sm text-blue-800 mt-1">
                {status?.review_message || (
                  <>You&apos;ve submitted everything Stripe needs. This review usually takes 1-3 business days:
                  charges and payouts will switch on automatically once it completes. No further action needed from you.</>
                )}
              </AlertDescription>
            </Alert>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
              <StatusPill label="Details submitted" ok={status?.details_submitted} />
              <StatusPill label="Charges enabled" ok={status?.charges_enabled} pending={!status?.charges_enabled} />
              <StatusPill label="Payouts enabled" ok={status?.payouts_enabled} pending={!status?.payouts_enabled} />
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" onClick={handleRefreshStatus} disabled={actionLoading} data-testid="stripe-refresh-status-btn">
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh Status
              </Button>
            </div>
          </>
        )}

        {/* iter1120 - Connected but Stripe has restricted the account */}
        {connected && restricted && (
          <>
            <Alert className="border-red-300 bg-red-50" data-testid="stripe-restricted-alert">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <AlertTitle className="text-red-900">Stripe has restricted this account</AlertTitle>
              <AlertDescription className="text-sm text-red-800 mt-1">
                {status?.review_message || 'Please open the Stripe Dashboard and contact Stripe Support to resolve.'}
              </AlertDescription>
            </Alert>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" onClick={handleDashboard} disabled={actionLoading} data-testid="stripe-dashboard-btn">
                <ExternalLink className="w-4 h-4 mr-2" /> Open Stripe Dashboard
              </Button>
              <Button variant="ghost" onClick={handleRefreshStatus} disabled={actionLoading} data-testid="stripe-refresh-status-btn">
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh Status
              </Button>
            </div>
          </>
        )}

        {/* Connected but onboarding incomplete (user action needed) */}
        {connected && !fullySetUp && !underReview && !restricted && (
          <>
            <Alert className="border-amber-300 bg-amber-50">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertTitle className="text-amber-900">Stripe onboarding incomplete</AlertTitle>
              <AlertDescription className="text-sm text-amber-800 mt-1">
                {outstandingItems.length > 0 ? (
                  <>
                    Stripe needs the following before charges &amp; payouts can switch on:
                    <ul className="mt-2 space-y-1.5 list-none" data-testid="stripe-requirements-checklist">
                      {outstandingItems.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-600 shrink-0" />
                          <div>
                            <div className="font-medium text-amber-900" data-testid={`stripe-req-label-${idx}`}>{item.label}</div>
                            {item.hint ? <div className="text-xs text-amber-700 mt-0.5">{item.hint}</div> : null}
                          </div>
                        </li>
                      ))}
                    </ul>
                    <p className="mt-3 text-xs text-amber-700">
                      {isStandardConnect
                        ? <>Because you signed in with your existing Stripe account, these details are managed inside your Stripe Dashboard, not here.</>
                        : <>Click <em>Resume Onboarding</em> to complete these on Stripe. Your progress is saved.</>}
                    </p>
                  </>
                ) : (
                  <>Your Stripe account exists but a few requirements remain. Resume the onboarding to start accepting payments.</>
                )}
              </AlertDescription>
            </Alert>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
              <StatusPill label="Details submitted" ok={status?.details_submitted} />
              <StatusPill label="Charges enabled" ok={status?.charges_enabled} />
              <StatusPill label="Payouts enabled" ok={status?.payouts_enabled} />
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={handleResume}
                disabled={actionLoading}
                className="bg-[#635BFF] hover:bg-[#5249e0] text-white"
                data-testid="stripe-resume-onboarding-btn"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}
                {isStandardConnect ? 'Open Stripe Dashboard' : 'Resume Onboarding'}
              </Button>
              <Button variant="outline" onClick={handleRefreshStatus} disabled={actionLoading} data-testid="stripe-refresh-status-btn">
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh Status
              </Button>
            </div>
          </>
        )}

        {/* Fully connected */}
        {fullySetUp && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
              <StatusPill label="Details submitted" ok />
              <StatusPill label="Charges enabled" ok />
              <StatusPill label="Payouts enabled" ok />
            </div>
            <p className="text-sm text-muted-foreground">
              Stripe is fully connected. Card payments on your invoices will be processed by Stripe and settled to your
              connected bank account.
            </p>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" onClick={handleDashboard} disabled={actionLoading} data-testid="stripe-dashboard-btn">
                <ExternalLink className="w-4 h-4 mr-2" /> Open Stripe Dashboard
              </Button>
              <Button variant="ghost" onClick={handleRefreshStatus} disabled={actionLoading} data-testid="stripe-refresh-status-btn">
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

const StripeLogo = () => (
  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="#635BFF">
    <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
  </svg>
);

const StatusPill = ({ label, ok, pending }) => {
  // iter1120 - `pending` (blue) sits between `ok` (green) and default (grey):
  // used when Stripe is verifying that specific capability. Prevents users
  // from reading "Charges enabled ⚠" as "you did something wrong".
  if (ok) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-md text-xs border border-emerald-200 bg-emerald-50 text-emerald-800">
        <CheckCircle className="w-3.5 h-3.5" />
        <span>{label}</span>
      </div>
    );
  }
  if (pending) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-md text-xs border border-blue-200 bg-blue-50 text-blue-800" data-testid={`status-pill-pending-${label.toLowerCase().replace(/\s+/g, '-')}`}>
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        <span>{label} <span className="opacity-70">(verifying)</span></span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-md text-xs border border-slate-200 bg-slate-50 text-slate-600">
      <AlertTriangle className="w-3.5 h-3.5" />
      <span>{label}</span>
    </div>
  );
};

export default StripeConnectSettings;
